import React, { useState, useEffect } from 'react';
import TemplateDownload from './TemplateDownload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function FleetData({ token, user }) {
  const [activeTab, setActiveTab] = useState('profiles');
  const [profiles, setProfiles] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [fuelRecords, setFuelRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Modal states
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [editingProfile, setEditingProfile] = useState(null);
  
  // Form states
  const [profileForm, setProfileForm] = useState({
    name: '',
    description: '',
    minor_interval_km: 10000,
    minor_interval_months: 3,
    minors_before_major: 3,
    major_additional_items: ''
  });
  
  const [apiKeyForm, setApiKeyForm] = useState({
    name: '',
    permissions: ['read']
  });

  useEffect(() => {
    if (activeTab === 'profiles') fetchProfiles();
    if (activeTab === 'api-keys') fetchApiKeys();
    if (activeTab === 'fuel') fetchFuelRecords();
  }, [activeTab]);

  const fetchProfiles = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/servicing-profiles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setProfiles(await res.json());
    } catch (err) {
      console.error('Error fetching profiles:', err);
    }
  };

  const fetchApiKeys = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/api-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setApiKeys(await res.json());
    } catch (err) {
      console.error('Error fetching API keys:', err);
    }
  };

  const fetchFuelRecords = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/fuel/all?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFuelRecords(data.records || []);
      }
    } catch (err) {
      console.error('Error fetching fuel records:', err);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const url = editingProfile 
        ? `${BACKEND_URL}/api/servicing-profiles/${editingProfile.id}`
        : `${BACKEND_URL}/api/servicing-profiles`;
      
      const res = await fetch(url, {
        method: editingProfile ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileForm)
      });
      
      if (res.ok) {
        setMessage({ type: 'success', text: `Profile ${editingProfile ? 'updated' : 'created'} successfully` });
        setShowProfileModal(false);
        setEditingProfile(null);
        setProfileForm({
          name: '',
          description: '',
          minor_interval_km: 10000,
          minor_interval_months: 3,
          minors_before_major: 3,
          major_additional_items: ''
        });
        fetchProfiles();
      } else {
        const err = await res.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to save profile' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save profile' });
    }
    setLoading(false);
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Are you sure you want to delete this profile?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/servicing-profiles/${profileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setMessage({ type: 'success', text: 'Profile deleted' });
        fetchProfiles();
      } else {
        const err = await res.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to delete' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete profile' });
    }
  };

  const handleCreateApiKey = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiKeyForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setNewApiKey(data);
        setApiKeyForm({ name: '', permissions: ['read'] });
        fetchApiKeys();
      } else {
        const err = await res.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to create API key' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to create API key' });
    }
    setLoading(false);
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setMessage({ type: 'success', text: 'API key revoked' });
        fetchApiKeys();
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to revoke API key' });
    }
  };

  const handleFileUpload = async (type, file) => {
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const endpoint = type === 'mileage' ? '/api/mileage/upload-excel' : '/api/fuel/upload-excel';
      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      if (res.ok) {
        setMessage({ 
          type: 'success', 
          text: `Uploaded ${data.success_count} records. ${data.error_count} errors.` 
        });
        if (type === 'fuel') fetchFuelRecords();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Upload failed' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Upload failed' });
    }
    setLoading(false);
  };

  const downloadTemplate = async (type) => {
    const endpoint = type === 'mileage' ? '/api/mileage/template' : '/api/fuel/template';
    window.open(`${BACKEND_URL}${endpoint}?token=${token}`, '_blank');
  };

  const tabs = [
    { id: 'profiles', label: 'Servicing Profiles', icon: '⚙️' },
    { id: 'mileage', label: 'Mileage Upload', icon: '📊' },
    { id: 'fuel', label: 'Fuel Records', icon: '⛽' },
    { id: 'api-keys', label: 'API Keys', icon: '🔑' }
  ];

  return (
    <div data-testid="fleet-data-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2>Fleet Data Management</h2>
          <p>Manage servicing profiles, upload mileage & fuel data, configure external APIs</p>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #E2E8F0', paddingBottom: '0' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            style={{
              padding: '0.75rem 1.25rem',
              background: activeTab === tab.id ? 'white' : 'transparent',
              border: activeTab === tab.id ? '2px solid #E2E8F0' : 'none',
              borderBottom: activeTab === tab.id ? '2px solid white' : 'none',
              marginBottom: '-2px',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? '600' : '400',
              color: activeTab === tab.id ? '#003366' : '#64748B'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Servicing Profiles Tab */}
      {activeTab === 'profiles' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0 }}>Servicing Profiles</h3>
            <button 
              className="btn btn-primary"
              onClick={() => {
                setEditingProfile(null);
                setProfileForm({
                  name: '',
                  description: '',
                  minor_interval_km: 10000,
                  minor_interval_months: 3,
                  minors_before_major: 3,
                  major_additional_items: ''
                });
                setShowProfileModal(true);
              }}
              data-testid="add-profile-btn"
            >
              + Add Profile
            </button>
          </div>
          
          <p style={{ color: '#64748B', marginBottom: '1rem' }}>
            Create profiles with servicing rules, then assign them to vehicles. Each profile defines minor/major service intervals.
          </p>

          {profiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚙️</div>
              <p>No profiles yet. Create your first servicing profile.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
              {profiles.map(profile => (
                <div 
                  key={profile.id} 
                  style={{ 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '12px', 
                    padding: '1.25rem',
                    background: '#FAFBFC'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0, color: '#003366' }}>{profile.name}</h4>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        className="btn btn-secondary" 
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                        onClick={() => {
                          setEditingProfile(profile);
                          setProfileForm(profile);
                          setShowProfileModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-danger" 
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                        onClick={() => handleDeleteProfile(profile.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {profile.description && (
                    <p style={{ color: '#64748B', fontSize: '0.875rem', marginBottom: '1rem' }}>{profile.description}</p>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.85rem' }}>
                    <div><strong>Minor Interval:</strong></div>
                    <div>{profile.minor_interval_km?.toLocaleString()} km or {profile.minor_interval_months} months</div>
                    <div><strong>Minors before Major:</strong></div>
                    <div>{profile.minors_before_major}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mileage Upload Tab */}
      {activeTab === 'mileage' && (
        <div className="card">
          <h3 style={{ marginBottom: '1rem' }}>Upload Mileage Data</h3>
          <p style={{ color: '#64748B', marginBottom: '1.5rem' }}>
            Upload vehicle mileage readings from an Excel file. The system will track historical mileage and update current readings.
          </p>
          
          <div style={{ 
            border: '2px dashed #CBD5E1', 
            borderRadius: '12px', 
            padding: '2rem', 
            textAlign: 'center',
            background: '#F8FAFC'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
            <p style={{ marginBottom: '1rem' }}>Upload Excel/CSV file with columns: <strong>vehicle_no, mileage, date (optional)</strong></p>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
              <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                📤 Upload Mileage File
                <input 
                  type="file" 
                  accept=".xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files[0] && handleFileUpload('mileage', e.target.files[0])}
                  data-testid="mileage-upload-input"
                />
              </label>
              <TemplateDownload templateType="mileage" token={token} compact={true} label="Sample Template" />
            </div>
          </div>
        </div>
      )}

      {/* Fuel Records Tab */}
      {activeTab === 'fuel' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Fuel Records</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                📤 Upload Fuel Data
                <input 
                  type="file" 
                  accept=".xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files[0] && handleFileUpload('fuel', e.target.files[0])}
                  data-testid="fuel-upload-input"
                />
              </label>
              <TemplateDownload templateType="fuel" token={token} compact={true} label="Template" />
            </div>
          </div>
          
          <p style={{ color: '#64748B', marginBottom: '1rem' }}>
            Upload fuel top-ups with columns: <strong>vehicle_no, odo_reading, liters, cost (optional), date (optional), station (optional)</strong>
          </p>

          {fuelRecords.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⛽</div>
              <p>No fuel records yet. Upload fuel data to start tracking.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Vehicle</th>
                    <th>ODO Reading</th>
                    <th>Liters</th>
                    <th>Cost</th>
                    <th>Station</th>
                  </tr>
                </thead>
                <tbody>
                  {fuelRecords.slice(0, 50).map((r, i) => (
                    <tr key={i}>
                      <td>{r.date}</td>
                      <td><span className="vin-plate">{r.vehicle_no}</span></td>
                      <td>{r.odo_reading?.toLocaleString()} km</td>
                      <td>{r.liters?.toFixed(1)} L</td>
                      <td>{r.cost ? `$${r.cost.toFixed(2)}` : '-'}</td>
                      <td>{r.station || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* API Keys Tab */}
      {activeTab === 'api-keys' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>External API Keys</h3>
            <button 
              className="btn btn-primary"
              onClick={() => setShowApiKeyModal(true)}
              data-testid="create-api-key-btn"
            >
              + Create API Key
            </button>
          </div>
          
          <p style={{ color: '#64748B', marginBottom: '1rem' }}>
            Create API keys for external systems to push/pull data (mileage, fuel, vehicles, repairs).
          </p>

          <div style={{ 
            background: '#EFF6FF', 
            border: '1px solid #BFDBFE', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginBottom: '1.5rem',
            fontSize: '0.875rem'
          }}>
            <strong>API Endpoints:</strong>
            <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
              <li><code>GET /api/external/vehicles</code> - List all vehicles</li>
              <li><code>POST /api/external/mileage</code> - Push mileage updates</li>
              <li><code>POST /api/external/fuel</code> - Push fuel records</li>
              <li><code>GET /api/external/servicing-due</code> - Get vehicles due for service</li>
              <li><code>GET /api/external/repairs</code> - Get repair records</li>
            </ul>
            <p style={{ margin: '0.5rem 0 0' }}>Use header: <code>X-API-Key: your_api_key</code></p>
          </div>

          {apiKeys.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔑</div>
              <p>No API keys created yet.</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key Preview</th>
                  <th>Permissions</th>
                  <th>Created</th>
                  <th>Last Used</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map(key => (
                  <tr key={key.id}>
                    <td><strong>{key.name}</strong></td>
                    <td><code style={{ fontSize: '0.8rem' }}>{key.key_preview}</code></td>
                    <td>{key.permissions?.join(', ')}</td>
                    <td>{key.created_at?.split('T')[0]}</td>
                    <td>{key.last_used ? key.last_used.split('T')[0] : 'Never'}</td>
                    <td>
                      <button 
                        className="btn btn-danger" 
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                        onClick={() => handleRevokeApiKey(key.id)}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingProfile ? 'Edit Profile' : 'Create Servicing Profile'}</h3>
              <button onClick={() => setShowProfileModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Profile Name *</label>
                <input 
                  className="form-input"
                  value={profileForm.name}
                  onChange={e => setProfileForm({...profileForm, name: e.target.value})}
                  placeholder="e.g., Standard, Heavy Duty, Light Vehicle"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input 
                  className="form-input"
                  value={profileForm.description}
                  onChange={e => setProfileForm({...profileForm, description: e.target.value})}
                  placeholder="Optional description"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Minor Interval (km)</label>
                  <input 
                    type="number"
                    className="form-input"
                    value={profileForm.minor_interval_km}
                    onChange={e => setProfileForm({...profileForm, minor_interval_km: parseInt(e.target.value) || 0})}
                    data-testid="minor-km-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Minor Interval (months)</label>
                  <input 
                    type="number"
                    className="form-input"
                    value={profileForm.minor_interval_months}
                    onChange={e => setProfileForm({...profileForm, minor_interval_months: parseInt(e.target.value) || 0})}
                    data-testid="minor-months-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Minor Services before Major</label>
                <input 
                  type="number"
                  className="form-input"
                  value={profileForm.minors_before_major}
                  onChange={e => setProfileForm({...profileForm, minors_before_major: parseInt(e.target.value) || 3})}
                  data-testid="minors-before-major-input"
                />
                <small style={{ color: '#64748B' }}>How many minor services before scheduling a major service</small>
              </div>
              <div className="form-group">
                <label className="form-label">Major Service Additional Items</label>
                <textarea 
                  className="form-textarea"
                  value={profileForm.major_additional_items || ''}
                  onChange={e => setProfileForm({...profileForm, major_additional_items: e.target.value})}
                  placeholder="What extra items are included in major vs minor service?"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowProfileModal(false)}>Cancel</button>
              <button 
                className="btn btn-primary" 
                onClick={handleSaveProfile}
                disabled={loading || !profileForm.name}
                data-testid="save-profile-btn"
              >
                {loading ? 'Saving...' : (editingProfile ? 'Update' : 'Create')} Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Key Modal */}
      {showApiKeyModal && (
        <div className="modal-overlay" onClick={() => { setShowApiKeyModal(false); setNewApiKey(null); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Create API Key</h3>
              <button onClick={() => { setShowApiKeyModal(false); setNewApiKey(null); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            </div>
            <div className="modal-body">
              {newApiKey ? (
                <div>
                  <div style={{ 
                    background: '#D1FAE5', 
                    border: '1px solid #6EE7B7', 
                    borderRadius: '8px', 
                    padding: '1rem', 
                    marginBottom: '1rem' 
                  }}>
                    <strong>✅ API Key Created!</strong>
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>Save this key now - it won't be shown again!</p>
                  </div>
                  <div className="form-group">
                    <label className="form-label">API Key</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input 
                        className="form-input"
                        value={newApiKey.key}
                        readOnly
                        style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                      />
                      <button 
                        className="btn btn-secondary"
                        onClick={() => {
                          navigator.clipboard.writeText(newApiKey.key);
                          setMessage({ type: 'success', text: 'Copied to clipboard!' });
                        }}
                      >
                        📋 Copy
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#64748B' }}>
                    <p><strong>Name:</strong> {newApiKey.name}</p>
                    <p><strong>Permissions:</strong> {newApiKey.permissions?.join(', ')}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Key Name *</label>
                    <input 
                      className="form-input"
                      value={apiKeyForm.name}
                      onChange={e => setApiKeyForm({...apiKeyForm, name: e.target.value})}
                      placeholder="e.g., GPS Tracking System, Fuel Management"
                      data-testid="api-key-name-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Permissions</label>
                    <div style={{ display: 'grid', gap: '0.5rem' }}>
                      {['read', 'write', 'mileage', 'fuel', 'vehicles'].map(perm => (
                        <label key={perm} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input 
                            type="checkbox"
                            checked={apiKeyForm.permissions.includes(perm)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setApiKeyForm({...apiKeyForm, permissions: [...apiKeyForm.permissions, perm]});
                              } else {
                                setApiKeyForm({...apiKeyForm, permissions: apiKeyForm.permissions.filter(p => p !== perm)});
                              }
                            }}
                          />
                          <span style={{ textTransform: 'capitalize' }}>{perm}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowApiKeyModal(false); setNewApiKey(null); }}>
                {newApiKey ? 'Close' : 'Cancel'}
              </button>
              {!newApiKey && (
                <button 
                  className="btn btn-primary" 
                  onClick={handleCreateApiKey}
                  disabled={loading || !apiKeyForm.name}
                  data-testid="create-key-btn"
                >
                  {loading ? 'Creating...' : 'Create API Key'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FleetData;
